import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["compareSeats"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { CompareSeatsModal } from 'pricing-pages-ui-components/compare-seats/CompareSeatsModal';
import { FscCreditPacksCard } from 'pricing-pages-ui-components/fsc-configure/FscCreditPacksCard';
import { FscOpsSeatDependencyBanner } from 'pricing-pages-ui-components/fsc-configure/FscOpsSeatDependencyBanner';
import { FscSeatLimitBanner } from 'pricing-pages-ui-components/fsc-configure/FscSeatLimitBanner';
import { FscSeatsCard } from 'pricing-pages-ui-components/fsc-configure/FscSeatsCard';
import { FscTierSelector } from 'pricing-pages-ui-components/fsc-configure/FscTierSelector';
import { HsocBodyBackgroundStyles } from 'pricing-pages-ui-components/stylesheets/HsocBodyBackground.styles';
import { BillingTermToggle } from 'pricing-pages-ui-components/toggle/BillingTermToggle';
import { useEffect, useRef } from 'react';
import { NavMarker } from 'react-rhumb';
import { FSC_CONFIGURE_PAGE } from '../../components/error-boundary/ErrorBoundaryNames';
import { PublicMerchCardsError } from '../../components/merch-card/PublicMerchCardsError';
import { PublicPageWrapper } from '../../components/PublicPageWrapper';
import { Screens } from '../../types/PublicNavItem';
import { trackPageView } from '../../utils/tracking';
import { PublicFscOrderSummary } from './PublicFscOrderSummary';
import { usePublicFscConfigurePage } from './usePublicFscConfigurePage';
import { useReadyFscConfigurePage } from './useReadyFscConfigurePage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function renderSeatsBanner({
  bannerConfig,
  onUpgradeFromBanner,
  onDismissOpsBanner
}) {
  switch (bannerConfig.type) {
    case 'seatLimitReached':
      return /*#__PURE__*/_jsx(FscSeatLimitBanner, {
        currentTier: bannerConfig.currentTier,
        maxQuantity: bannerConfig.maxQuantity,
        upgrade: bannerConfig.upgradeTier && {
          tier: bannerConfig.upgradeTier,
          cta: /*#__PURE__*/_jsx(Button, {
            use: "tertiary-light",
            size: "small",
            onClick: onUpgradeFromBanner,
            "data-selenium-id": "fsc-configure-seat-limit-upgrade",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "publicFscConfigure.seatLimitBanner.upgradeCta"
            })
          })
        }
      });
    case 'opsSeatDependency':
      return /*#__PURE__*/_jsx(FscOpsSeatDependencyBanner, {
        onClose: onDismissOpsBanner
      });
    case 'none':
      return null;
    // no default
  }
}
function FscConfigureTermToggle({
  isAnnual,
  onToggle
}) {
  return /*#__PURE__*/_jsx(BillingTermToggle, {
    isAnnual: isAnnual,
    onToggle: onToggle,
    labelAnnual: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "publicFscConfigure.termToggle.annual"
    }),
    labelMonthly: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "publicFscConfigure.termToggle.monthly"
    }),
    ariaLabel: isAnnual ? unescapedText('publicFscConfigure.termToggle.ariaLabelAnnual') : unescapedText('publicFscConfigure.termToggle.ariaLabelMonthly'),
    "data-selenium-id": "fsc-configure-term-toggle"
  });
}
function FscConfigureHeader({
  termToggle
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "400",
    marginBlockEnd: "600",
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h1",
      typography: "heading-400",
      marginBlock: "0",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "publicFscConfigure.title"
      })
    }), termToggle]
  });
}
function ReadyFscConfigureContent(_ref) {
  let {
      compareSeats
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    tierSelector,
    seatsCard,
    creditPacks,
    termToggle,
    orderSummary
  } = useReadyFscConfigurePage(props);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FscConfigureHeader, {
      termToggle: termToggle.show && /*#__PURE__*/_jsx(FscConfigureTermToggle, Object.assign({}, termToggle))
    }), /*#__PURE__*/_jsxs(Grid, {
      children: [/*#__PURE__*/_jsx(GridItem, {
        size: {
          xs: 12,
          lg: 8
        },
        marginBlockEnd: "400",
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "400",
          children: [/*#__PURE__*/_jsxs(CardWrapper, {
            inlineSize: "100%",
            padding: "600",
            children: [/*#__PURE__*/_jsx(NavMarker, {
              name: "FSC_CONFIGURE_CONTENT"
            }), /*#__PURE__*/_jsx(FscTierSelector, Object.assign({}, tierSelector)), compareSeats && /*#__PURE__*/_jsx(View, {
              marginBlockStart: "200",
              children: /*#__PURE__*/_jsx(CompareSeatsModal, Object.assign({}, compareSeats))
            })]
          }), /*#__PURE__*/_jsx(FscSeatsCard, {
            status: seatsCard.status,
            banner: renderSeatsBanner(seatsCard)
          }), creditPacks && /*#__PURE__*/_jsx(FscCreditPacksCard, {
            status: creditPacks
          })]
        })
      }), /*#__PURE__*/_jsx(GridItem, {
        size: {
          xs: 12,
          lg: 4
        },
        marginBlockEnd: "400",
        children: /*#__PURE__*/_jsx(PublicFscOrderSummary, Object.assign({}, orderSummary))
      })]
    })]
  });
}
function PublicFscConfigureContent() {
  const viewModel = usePublicFscConfigurePage();
  return /*#__PURE__*/_jsxs(View, {
    maxInlineSize: "1180px",
    marginInline: "auto",
    paddingBlock: "800",
    paddingInline: "400",
    children: [/*#__PURE__*/_jsx(View, {
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(Link, {
        href: viewModel.backToPricingHref,
        onClick: viewModel.onBackToPricing,
        "data-selenium-id": "fsc-configure-back-to-pricing",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "publicFscConfigure.backToPricing"
        })
      })
    }), viewModel.status === 'ready' ? /*#__PURE__*/_jsx(ReadyFscConfigureContent, Object.assign({}, viewModel.readyPage, {
      compareSeats: viewModel.compareSeats
    })) : /*#__PURE__*/_jsx(FscConfigureHeader, {
      termToggle: null
    }), viewModel.status === 'loading' && /*#__PURE__*/_jsx(View, {
      paddingBlock: "800",
      children: /*#__PURE__*/_jsx(LoadingSpinner, {
        size: "medium"
      })
    }), viewModel.status === 'error' && /*#__PURE__*/_jsx(PublicMerchCardsError, {}), viewModel.status === 'unavailable' && /*#__PURE__*/_jsxs(CardWrapper, {
      inlineSize: "100%",
      padding: "600",
      children: [/*#__PURE__*/_jsx(NavMarker, {
        name: "FSC_CONFIGURE_CONTENT"
      }), /*#__PURE__*/_jsx(Text, {
        as: "h2",
        typography: "heading-200",
        marginBlockStart: "0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "publicFscConfigure.unavailable.title"
        })
      }), /*#__PURE__*/_jsx(Text, {
        as: "p",
        color: "text-core-subtle",
        marginBlockStart: "100",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "publicFscConfigure.unavailable.description"
        })
      })]
    })]
  });
}
export default function PublicFscConfigurePage() {
  const hasTrackedPageViewRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedPageViewRef.current) {
      hasTrackedPageViewRef.current = true;
      trackPageView({
        screen: Screens.FSC_CONFIGURE
      });
    }
  }, []);
  return /*#__PURE__*/_jsxs(PublicPageWrapper, {
    errorBoundaryName: FSC_CONFIGURE_PAGE,
    navMarkerName: "FSC_CONFIGURE_PAGE",
    metricsRoute: Screens.FSC_CONFIGURE,
    chunkName: "public-fsc-configure-page",
    children: [/*#__PURE__*/_jsx(HsocBodyBackgroundStyles, {}), /*#__PURE__*/_jsx(PublicFscConfigureContent, {})]
  });
}